import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
    ReactNode,
} from "react";

import {
    FormContextState,
    FormMode,
    FormData,
    FormErrors,
    DirtyMap,
    FormValidationFunction,
} from "./formTypes";

import { TableField } from "../../types/interfaces";
import { validateFieldValue } from "../../utils/validators";

// ==== CONTEXT CREATION ====

const FormContext = createContext<FormContextState | undefined>(undefined);

export const useFormContext = (): FormContextState => {
    const ctx = useContext(FormContext);
    if (!ctx) throw new Error("useFormContext must be used within a <FormProvider>");
    return ctx;
};

// ==== PROVIDER ====

interface Props {
    mode: FormMode;
    fields: TableField[];
    initialData?: FormData;
    children: ReactNode;
}

export const FormProvider: React.FC<Props> = ({
    mode,
    fields,
    initialData = {},
    children,
}) => {
    const [formData, setFormData] = useState<FormData>(() => {
        const base: FormData = {};
        fields.forEach(field => {
            base[field.fieldID] = initialData[field.fieldID] ?? "";
        });
        return base;
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [dirty, setDirty] = useState<DirtyMap>({});
    const [touched, setTouched] = useState<DirtyMap>({});
    const [customValidators, setCustomValidators] = useState<FormValidationFunction[]>([]);

    const [readonly, setReadonly] = useState<DirtyMap>({});
    const [visible, setVisible] = useState<DirtyMap>(() => {
        const vis: DirtyMap = {};
        fields.forEach(f => vis[f.fieldID] = true); // default visible
        return vis;
    });

    const [focusedFieldId, setFocusedFieldId] = useState<string | null>(null);

    // ==== GETTERS / HELPERS ====

    const getValue = useCallback((fieldId: string) => {
        return formData[fieldId];
    }, [formData]);

    const getFieldDef = useCallback((fieldId: string): TableField | undefined => {
        return fields.find(f => f.fieldID === fieldId);
    }, [fields]);

    const getFocusedField = () => focusedFieldId;

    // ==== STATE MUTATORS ====

    const setValue = useCallback((fieldId: string, value: any) => {
        if (!fields.find(f => f.fieldID === fieldId)) return;

        setFormData(prev => ({ ...prev, [fieldId]: value }));
        setDirty(prev => ({ ...prev, [fieldId]: true }));
        setTouched(prev => ({ ...prev, [fieldId]: true }));

        const fieldDef = getFieldDef(fieldId);
        if (fieldDef) {
            const result = validateFieldValue(fieldDef, value);
            setErrors(prev => ({ ...prev, [fieldId]: result.valid ? undefined : result.message }));
        }
    }, [fields, getFieldDef]);

    const setError = useCallback((fieldId: string, error: string | undefined) => {
        setErrors(prev => ({ ...prev, [fieldId]: error }));
    }, []);

    const setFieldVisible = useCallback((fieldId: string, show: boolean) => {
        setVisible(prev => ({ ...prev, [fieldId]: show }));
    }, []);

    const setFieldReadonly = useCallback((fieldId: string, readonlyState: boolean) => {
        setReadonly(prev => ({ ...prev, [fieldId]: readonlyState }));
    }, []);

    const setFocusedField = useCallback((fieldId: string | null) => {
        setFocusedFieldId(fieldId);
    }, []);

    // ==== VALIDATION ====

    const validate = useCallback(() => {
        let isValid = true;
        const nextErrors: FormErrors = {};

        for (const field of fields) {
            const result = validateFieldValue(field, formData[field.fieldID]);
            if (!result.valid) {
                nextErrors[field.fieldID] = result.message;
                isValid = false;
            }
        }

        for (const validator of customValidators) {
            const result = validator(formData);
            if (!result.valid) {
                nextErrors[result.fieldID] = result.message;
                isValid = false;
            }
        }

        setErrors(nextErrors);
        return isValid;
    }, [formData, fields, customValidators]);

    const addCustomValidator = useCallback((fn: FormValidationFunction) => {
        setCustomValidators(prev => [...prev, fn]);
    }, []);

    const reset = useCallback(() => {
        const resetData: FormData = {};
        fields.forEach(field => {
            resetData[field.fieldID] = initialData[field.fieldID] ?? "";
        });
        setFormData(resetData);
        setErrors({});
        setDirty({});
        setTouched({});
        setFocusedFieldId(null);
    }, [fields, initialData]);

    const updateRawData = useCallback((newData: FormData) => {
        const updated: FormData = {};
        fields.forEach(field => {
            updated[field.fieldID] = newData[field.fieldID] ?? "";
        });
        setFormData(updated);
        setDirty({});
        setTouched({});
        setFocusedFieldId(null);
    }, [fields]);

    // ==== CONTEXT VALUE ====

    const value: FormContextState = useMemo(() => ({
        mode,
        data: formData,
        errors,
        dirty,
        touched,
        getValue,
        setValue,
        setError,
        reset,
        validate,
        updateRawData,
        getFieldDef,
        setFieldVisible,
        setFieldReadonly,
        addCustomValidator,
        visible,
        readonly,
        fields,
        focusedFieldId,
        setFocusedField,
        getFocusedField
    }), [
        mode, formData, errors, dirty, touched,
        getValue, setValue, setError,
        reset, validate, updateRawData,
        getFieldDef, setFieldVisible, setFieldReadonly,
        addCustomValidator, visible, readonly, fields,
        focusedFieldId, setFocusedField, getFocusedField
    ]);

    return (
        <FormContext.Provider value={value}>
            {children}
        </FormContext.Provider>
    );
};
